import React from 'react';
import { Box, Flex, Heading, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Buildings,
  ClipboardText,
  FileDoc,
  GraduationCap,
  Scales,
  Users,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../components/glass';
import { usePermittedSubmodules } from '../../utils/getSubmodulesForPath';
import { useSocietario } from '../../hooks/useSocietario';
import { IPrivateRoutes } from '../../routes/private.routes';

const MODULE_NAME = 'Societário';
const MODULE_DESCRIPTION =
  'Gere, versione e acompanhe contratos de constituição e alteração societária com auxílio da IA.';
const MODULE_COLOR = '#EDE9FE';
const MODULE_ICON_COLOR = '#7C3AED';
const BASE_PATH = '/societario';

/**
 * Mapeamento de submodulo -> icone exibido no card.
 * Caso o submodulo nao esteja aqui, usa um icone padrao.
 */
const SUBMODULE_ICONS: Record<string, React.ReactNode> = {
  '/societario/contratos': <ClipboardText size={26} color="#1976D2" weight="duotone" />,
  '/societario/templates': <FileDoc size={26} color="#1976D2" weight="duotone" />,
  '/societario/modelos': <FileDoc size={26} color="#7C3AED" weight="duotone" />,
  '/societario/socios': <Users size={26} color="#9C27B0" weight="duotone" />,
  '/societario/estatutos': <GraduationCap size={26} color="#7C3AED" weight="duotone" />,
  '/societario/legalizacao': <Buildings size={26} color="#7C3AED" weight="duotone" />,
};

const SUBMODULE_TINT: Record<string, string> = {
  '/societario/contratos': '#DBEAFE',
  '/societario/templates': '#E3F2FD',
  '/societario/modelos': '#EDE9FE',
  '/societario/socios': '#FCE7F3',
  '/societario/estatutos': '#EDE9FE',
  '/societario/legalizacao': '#EDE9FE',
};

const SUBMODULE_DESCRIPTION: Record<string, string> = {
  '/societario/contratos': 'Lista, gera e acompanha contratos societários.',
  '/societario/templates': 'Modelos DOCX por tipo de operação.',
  '/societario/modelos': 'Markdown editável de referência usado pela IA.',
  '/societario/socios': 'Base reutilizável de pessoas e dados de sócios.',
  '/societario/estatutos': 'Kanban de estatutos de comissão de formatura e assinatura.',
  '/societario/legalizacao': 'Acompanha a legalização na Junta — constituição, alteração e baixa.',
};

const SUBMODULE_PREVIEW: Record<string, React.ComponentType> = {
  '/societario/contratos': () => <ContratosPreview />,
};

const DEFAULT_ICON = <FileDoc size={26} color="#1B88F5" weight="duotone" />;

// ============================================================================
// Card Submodulo (mesmo padrao de accountingConciliation)
// ============================================================================

interface SubmoduleCardProps {
  sub: IPrivateRoutes;
  index: number;
}

const SubmoduleCard = ({ sub, index }: SubmoduleCardProps) => {
  const navigate = useNavigate();
  const description = SUBMODULE_DESCRIPTION[sub.path] ?? 'Acesse o submódulo';
  const PreviewComponent = SUBMODULE_PREVIEW[sub.path];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <GlassCard
        intensity="strong"
        hoverable
        p={6}
        cursor="pointer"
        onClick={() => navigate(sub.path)}
        h="full"
      >
        <Flex direction="column" gap={4} h="full">
          <Flex align="center" justify="space-between">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="52px"
              h="52px"
              borderRadius="2xl"
              bg={SUBMODULE_TINT[sub.path] ?? '#DFF3FF'}
              flexShrink={0}
            >
              {SUBMODULE_ICONS[sub.path] ?? DEFAULT_ICON}
            </Box>
            <Box opacity={0.5} transition="all 0.2s ease">
              <ArrowRight size={18} color="#1B88F5" weight="bold" />
            </Box>
          </Flex>

          <Box flex={1}>
            <Heading as="h3" fontSize="md" fontWeight="600" color="#0F172A" lineHeight="1.3" mb={1}>
              {sub.name}
            </Heading>
            <Text fontSize="xs" color="#71717A">
              {description}
            </Text>
          </Box>

          {PreviewComponent && <PreviewComponent />}
        </Flex>
      </GlassCard>
    </motion.div>
  );
};

// ============================================================================
// Preview com KPIs do submodulo Contratos
// ============================================================================

const ContratosPreview = () => {
  const { getContratos } = useSocietario();
  // Busca de uma pagina enxuta — usa size=100 pra ter dados suficientes pra
  // contar status sem paginacao no preview. Backend ja filtra arquivados
  // por default (Fase D).
  const { data, isFetching, isError } = useQuery({
    queryKey: ['societario-contratos-preview'],
    queryFn: () => getContratos({ queryKey: ['_', { page: 1, size: 100 }] } as any),
    staleTime: 2 * 60 * 1000,
  });

  if (isFetching && !data) {
    return (
      <Flex justify="center" py={3}>
        <Spinner size="sm" color="#1B88F5" thickness="2px" />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Box mt={1} pt={3} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
        <Text fontSize="10px" color="red.500">
          Falha ao carregar resumo. Tente novamente.
        </Text>
      </Box>
    );
  }
  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? items.length;
  const rascunhos = items.filter(c =>
    ['rascunho', 'rascunho_inicial', 'campos_pendentes'].includes(c.status),
  ).length;
  const finalizados = items.filter(c => ['gerado', 'assinado'].includes(c.status)).length;

  return (
    <Box mt={1} pt={4} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
      <SimpleGrid columns={3} spacing={3}>
        <Box>
          <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="medium">
            Total
          </Text>
          <Text fontSize="lg" fontWeight="700" color="#0F172A">
            {total}
          </Text>
        </Box>
        <Box>
          <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="medium">
            Rascunhos
          </Text>
          <Text fontSize="lg" fontWeight="700" color="#F59E0B">
            {rascunhos}
          </Text>
        </Box>
        <Box>
          <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="medium">
            Finalizados
          </Text>
          <Text fontSize="lg" fontWeight="700" color="#22C55E">
            {finalizados}
          </Text>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

// ============================================================================
// Pagina principal
// ============================================================================

export function SocietarioHomePage() {
  // Templates/Modelos (v1/v2 aposentados) somem via `hiddenSubmodule` na rota —
  // o filtro é central no `getSubmodulesForPath`, então vale p/ home + sidebar.
  const submodules = usePermittedSubmodules(BASE_PATH);

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        {/* Header do modulo */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={5} mb={3}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bg={MODULE_COLOR}
              boxShadow="0 8px 24px -4px rgba(124, 58, 237, 0.25)"
            >
              <Scales size={32} color={MODULE_ICON_COLOR} weight="duotone" />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Módulo
              </Text>
              <Heading
                fontSize={{ base: '24px', md: '32px' }}
                fontWeight="700"
                color="#0F172A"
                lineHeight="1.1"
                letterSpacing="-0.02em"
              >
                {MODULE_NAME}
              </Heading>
            </Box>
          </Flex>

          <Text fontSize="sm" color="#71717A" mb={10} ml={1} maxW="640px" lineHeight="1.6">
            {MODULE_DESCRIPTION}
          </Text>
        </motion.div>

        {/* Subtitulo dos submodulos */}
        <Flex align="center" gap={2} mb={5}>
          <Buildings size={18} color="#1B88F5" weight="duotone" />
          <Heading as="h2" fontSize="md" fontWeight="600" color="#0F172A" letterSpacing="-0.01em">
            Submódulos disponíveis
          </Heading>
          <Text fontSize="xs" color="#94A3B8" ml={1}>
            ({submodules.length})
          </Text>
        </Flex>

        {submodules.length === 0 ? (
          <GlassCard intensity="medium" p={10} textAlign="center">
            <Stack align="center" spacing={2}>
              <Text fontSize="md" color="#71717A">
                Nenhum submódulo disponível ainda.
              </Text>
              <Text fontSize="xs" color="#94A3B8">
                Submódulos serão exibidos aqui automaticamente quando criados.
              </Text>
            </Stack>
          </GlassCard>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
            {submodules.map((sub, idx) => (
              <SubmoduleCard key={sub.path} sub={sub} index={idx} />
            ))}
          </SimpleGrid>
        )}
      </Box>
    </GlassPageBg>
  );
}

export default SocietarioHomePage;
